import { useEffect, useState } from 'react'

import type {
    CarrierManualUnits,
    CarrierServiceOption,
} from '@/lib/api/carrier-shipment-api'
import type { CarrierQuoteContext } from '@/lib/carrier-quote-fingerprint'

import { carrierQuoteFingerprint } from '@/lib/carrier-quote-fingerprint'

interface Options {
    quoteContext: CarrierQuoteContext
    weightGram: number
    manualUnits?: CarrierManualUnits
    /** Old commune the label prints; a different one reprices the quote. */
    legacyWardId?: number
    /** Service the customer picked when ordering (web / Mini App). Each new
     *  quote that offers it starts with it selected; staff may still change. */
    preferredServiceCode?: string | null
}

export function useCarrierQuoteState({
    quoteContext,
    weightGram,
    manualUnits,
    legacyWardId,
    preferredServiceCode,
}: Options) {
    const [services, setServices] = useState<CarrierServiceOption[] | null>(
        null,
    )
    const [serviceCode, setServiceCode] = useState('')
    const [quotedFingerprint, setQuotedFingerprint] = useState<string | null>(
        null,
    )
    const [quoteInvalidated, setQuoteInvalidated] = useState(false)
    const currentFingerprint = carrierQuoteFingerprint(
        quoteContext,
        weightGram,
        manualUnits,
        legacyWardId,
    )
    const quoteMatches =
        quotedFingerprint !== null && quotedFingerprint === currentFingerprint
    const selectedServiceQuoted =
        quoteMatches &&
        services?.some((service) => service.service_code === serviceCode) ===
            true

    const invalidate = () => {
        if (services !== null || serviceCode || quotedFingerprint !== null) {
            setQuoteInvalidated(true)
        }
        setServices(null)
        setServiceCode('')
        setQuotedFingerprint(null)
    }

    useEffect(() => {
        if (
            quotedFingerprint !== null &&
            quotedFingerprint !== currentFingerprint
        ) {
            setQuoteInvalidated(true)
            setServices(null)
            setServiceCode('')
            setQuotedFingerprint(null)
        }
    }, [currentFingerprint, quotedFingerprint])

    const accept = (options: CarrierServiceOption[], fingerprint: string) => {
        setServices(options)
        // A new quote resets the choice, so staff have not picked for it yet:
        // start from the customer's service when this quote offers it.
        setServiceCode(
            preferredServiceCode &&
                options.some(
                    (option) => option.service_code === preferredServiceCode,
                )
                ? preferredServiceCode
                : '',
        )
        setQuotedFingerprint(fingerprint)
        setQuoteInvalidated(false)
    }

    return {
        currentFingerprint,
        services: quoteMatches ? services : null,
        serviceCode: selectedServiceQuoted ? serviceCode : '',
        selectedServiceQuoted,
        quoteInvalidated,
        selectService: setServiceCode,
        startQuote: () => {
            setServices(null)
            setServiceCode('')
        },
        invalidate,
        accept,
    }
}
